import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { gamificationService } from "@/services/gamificationService";
import { useAuth } from "@/contexts/AuthContext";
import {
  ListTodo,
  Plus,
  Trash2,
  Circle,
  Clock,
  AlertCircle,
} from "lucide-react";

interface LearningQueueProps {
  compact?: boolean;
}

interface QueueItem {
  id: string;
  userId: string;
  title: string;
  description?: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "pending" | "in_progress" | "completed";
  xpReward?: string;
  createdAt: Date;
}

const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", color: "#ef4444", icon: AlertCircle },
  high: { label: "High", color: "#f97316", icon: Clock },
  medium: { label: "Medium", color: "#eab308", icon: Circle },
  low: { label: "Low", color: "#22c55e", icon: Circle },
};

export function LearningQueue({ compact = false }: LearningQueueProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [newTask, setNewTask] = useState("");
  const [newPriority, setNewPriority] = useState<string>("medium");
  const [isAdding, setIsAdding] = useState(false);
  const [localQueue, setLocalQueue] = useState<QueueItem[]>([]);

  const { data: queue, isLoading } = useQuery({
    queryKey: ["learning-queue", user?.id],
    queryFn: () => gamificationService.getLearningQueue(user!.id),
    enabled: !!user?.id,
  });

  const combinedQueue = [...(queue || []), ...localQueue];

  const addMutation = useMutation({
    mutationFn: async () => {
      return gamificationService.addToLearningQueue({
        userId: user!.id,
        title: newTask,
        priority: newPriority as "low" | "medium" | "high" | "urgent",
        status: "pending",
      });
    },
    onMutate: async () => {
      const optimisticItem: QueueItem = {
        id: `temp-${Date.now()}`,
        userId: user!.id,
        title: newTask,
        priority: newPriority as "low" | "medium" | "high" | "urgent",
        status: "pending",
        xpReward: "5",
        createdAt: new Date(),
      };
      setLocalQueue(prev => [...prev, optimisticItem]);
      setNewTask("");
      setIsAdding(false);
    },
    onSuccess: () => {
      setLocalQueue([]);
      queryClient.invalidateQueries({ queryKey: ["learning-queue"] });
    },
    onError: () => {
      // Keep local items on error so they don't disappear
    },
  });

  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      return gamificationService.completeQueueItem(id, user!.id);
    },
    onMutate: async (id) => {
      setLocalQueue(prev => prev.filter(item => item.id !== id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["learning-queue"] });
      queryClient.invalidateQueries({ queryKey: ["game-stats"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return gamificationService.deleteQueueItem(id);
    },
    onMutate: async (id) => {
      setLocalQueue(prev => prev.filter(item => item.id !== id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["learning-queue"] });
    },
  });

  const handleAddTask = () => {
    if (newTask.trim()) {
      addMutation.mutate();
    }
  };

  const sortedQueue = combinedQueue.sort((a, b) => {
    const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
    return priorityOrder[a.priority as keyof typeof priorityOrder] - priorityOrder[b.priority as keyof typeof priorityOrder];
  });

  const urgentCount = sortedQueue.filter(q => q.priority === "urgent" || q.priority === "high").length;

  if (compact) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <div className="flex items-center gap-2">
              <ListTodo className="w-4 h-4 text-green-500" />
              Learning Queue
            </div>
            {urgentCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {urgentCount} priority
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {sortedQueue.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              No tasks in queue
            </p>
          ) : (
            <div className="space-y-2">
              {sortedQueue.slice(0, 3).map((item) => {
                const config = PRIORITY_CONFIG[item.priority as keyof typeof PRIORITY_CONFIG];
                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
                  >
                    <Checkbox
                      checked={false}
                      onCheckedChange={() => completeMutation.mutate(item.id)}
                    />
                    <span className="flex-1 text-sm truncate">{item.title}</span>
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: config.color }}
                    />
                  </div>
                );
              })}
              {sortedQueue.length > 3 && (
                <p className="text-xs text-muted-foreground text-center">
                  +{sortedQueue.length - 3} more tasks
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-green-500" />
            Learning Queue
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAdding(!isAdding)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Task
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isAdding && (
          <div className="p-3 rounded-lg border bg-muted/30 space-y-3">
            <Input
              placeholder="What do you want to learn or research?"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddTask()}
              autoFocus
            />
            <div className="flex items-center gap-2">
              <Select value={newPriority} onValueChange={setNewPriority}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: config.color }}
                        />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAddTask}
                disabled={!newTask.trim() || addMutation.isPending}
                className="flex-1"
              >
                Add to Queue
              </Button>
            </div>
          </div>
        )}

        {isLoading && localQueue.length === 0 ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : sortedQueue.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <ListTodo className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Your learning queue is empty</p>
            <p className="text-sm">Add tasks to track what you want to learn!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedQueue.map((item) => {
              const config = PRIORITY_CONFIG[item.priority as keyof typeof PRIORITY_CONFIG];
              const PriorityIcon = config.icon;
              const isTemp = item.id.startsWith('temp-');
              
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors group ${isTemp ? 'opacity-70' : ''}`}
                >
                  <Checkbox
                    checked={item.status === "completed"}
                    onCheckedChange={() => completeMutation.mutate(item.id)}
                    className="h-5 w-5"
                    disabled={isTemp}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${item.status === "completed" ? "line-through text-muted-foreground" : ""}`}>
                        {item.title}
                      </span>
                      {isTemp && <span className="text-xs text-muted-foreground">(saving...)</span>}
                    </div>
                    {item.description && (
                      <p className="text-sm text-muted-foreground truncate">
                        {item.description}
                      </p>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1"
                    style={{ borderColor: config.color, color: config.color }}
                  >
                    <PriorityIcon className="w-3 h-3" />
                    {config.label}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    +{item.xpReward || 5} XP
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMutation.mutate(item.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    disabled={isTemp}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {sortedQueue.length > 0 && (
          <div className="pt-4 border-t flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {sortedQueue.length} task{sortedQueue.length !== 1 ? "s" : ""} in queue
            </span>
            <span className="text-green-500 font-medium">
              Complete all for {sortedQueue.reduce((sum, q) => sum + parseInt(q.xpReward || "5", 10), 0)} XP
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
